import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { useToast } from '../../contexts/ToastContext';
import { useSignUpFeature, useMfaFeature } from '../../hooks/useFeatureFlags';
import { Card, CardHeader, CardContent } from '../../components/ui/card';
import { FormGroup, FormLabel, FormInput } from '../../components/ui/form';
import { MfaChallenge } from '../../components/auth';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mfaChallenge, setMfaChallenge] = useState(null);
  const [mfaLoading, setMfaLoading] = useState(false);
  const navigate = useNavigate();
  const addToast = useToast();
  const { isSignUpEnabled } = useSignUpFeature();
  const { isMfaAvailable } = useMfaFeature();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      
      if (error) {
        setError(error.message);
        setLoading(false);
        return;
      }

      // Check if MFA is required
      if (data.session && data.session.access_token) {
        // User is fully authenticated
        addToast('Login successful!', 'success');
        navigate('/');
      } else if (data.user && !data.session && isMfaAvailable) {
        // MFA challenge required (only if MFA is available)
        const { data: factors } = await supabase.auth.mfa.listFactors();
        const verifiedFactors = factors?.all?.filter(factor => factor.status === 'verified') || [];
        
        if (verifiedFactors.length > 0) {
          // Start MFA challenge with the first verified factor
          const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
            factorId: verifiedFactors[0].id
          });
          
          if (challengeError) {
            setError('Failed to start MFA challenge: ' + challengeError.message);
          } else {
            setMfaChallenge({
              challengeId: challengeData.id,
              factorId: verifiedFactors[0].id
            });
          }
        } else {
          setError('MFA is enabled but no verified factors found. Please contact support.');
        }
      } else {
        // Either no MFA required or MFA is not available
        addToast('Login successful!', 'success');
        navigate('/');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaVerification = async (verificationCode) => {
    setMfaLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.auth.mfa.verify({
        factorId: mfaChallenge.factorId,
        challengeId: mfaChallenge.challengeId,
        code: verificationCode
      });

      if (error) {
        setError(error.message || 'Invalid verification code');
      } else if (data.session) {
        addToast('Login successful!', 'success');
        navigate('/');
      } else {
        setError('MFA verification failed. Please try again.');
      }
    } catch (error) {
      console.error('MFA verification error:', error);
      setError('An unexpected error occurred during verification.');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleMfaCancel = () => {
    setMfaChallenge(null);
    setError(null);
  };

  // Show MFA challenge if required and MFA is available
  if (mfaChallenge && isMfaAvailable) {
    return (
      <div className="bg-gray-50 dark:bg-gray-900 flex justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <MfaChallenge
            onVerify={handleMfaVerification}
            onCancel={handleMfaCancel}
            loading={mfaLoading}
            error={error}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 dark:bg-gray-900 flex justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <Card>
          <CardHeader>
            <h1 className="text-center text-2xl font-bold text-gray-900 dark:text-gray-100">
              Sign in to your account
            </h1>
            <p className="text-center text-sm text-gray-600 dark:text-gray-400">
              Please enter your email and password to log in. If you don't have an account, you can sign up.
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-6">
              <FormGroup>
                <FormLabel htmlFor="email">Email address</FormLabel>
                <FormInput
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel htmlFor="password">Password</FormLabel>
                <FormInput
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </FormGroup>
              
              {error && (
                <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
                  <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
                </div>
              )}
              
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </button>
              
              <div className="flex items-center justify-between">
                <Link 
                  to="/forgot-password"
                  className="text-sm text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  Forgot your password?
                </Link>
                {isSignUpEnabled ? (
                  <Link 
                    to="/sign-up" 
                    className="text-sm text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    Sign up
                  </Link>
                ) : (
                  <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                    New Signups Disabled
                  </span>
                )}
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default Login;